
import { faApple, faGoogle } from '@fortawesome/free-brands-svg-icons'
import { faClock, faRoute } from '@fortawesome/pro-regular-svg-icons'
import { faCar as faCarSolid, faDog, faLocationArrow, faLocationXmark } from '@fortawesome/pro-solid-svg-icons'
import Vue from 'vue'

// We are manually importing our component so we can render it off-document and grab its html for a map marker
import MapIcon from '~/components/elements/geo/map/marker/MapIcon.vue'
import AddressEntity from '~/lib/entities/address'
import { getCurrentPosition } from '~/lib/geo/currentPosition'
import { openInGoogleMaps } from '~/lib/geo/openMaps'

const IconInstance = Vue.extend(MapIcon)

export default {
  name: 'MapDirections',

  props: {
    paddock: {
      type: Object,
      required: true
    },

    arriveAt: {
      type: String,
      default: null
    },

    hasZoomControls: {
      type: Boolean,
      default: false
    },

    isBrochureware: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      fetchDirectionsApi: new this.$ApiModel(),
      isMapReady: false,
      entranceMarker: null,
      startMarker: null,
      isSearchResultsVisible: false,
      isFetchingLocation: false,
      address: {
        ...AddressEntity().model
      },

      directions: [],
      routeOverview: {},
      faLocationArrow,
      faLocationXmark,
      faGoogle,
      faClock,
      faRoute,
      faApple
    }
  },

  computed: {
    compMap() {
      return this.$refs.directionsMap?.map
    }
  },

  methods: {
    onMapReady() {
      this.isMapReady = true

      this.setEntranceMarker(this.paddock.entranceCoordinates)
      this.addField(this.paddock.fieldPolygon)

      setTimeout(() => {
        this.compMap.flyTo({
          speed: 0.5,
          center: this.paddock.fieldCenterPoint || this.paddock.entranceCoordinates,
          zoom: 15
        })
      }, 500)
    },

    reset() {
      this.directions = []
      this.address = { ...AddressEntity().model }

      this.startMarker.remove()
      this.startMarker = null

      this.compMap.removeLayer('route')
      this.compMap.removeSource('route')

      this.compMap.flyTo({ center: this.paddock.fieldCenterPoint || this.paddock.entranceCoordinates, zoom: 15 })

      this.resize()
    },

    async resize() {
      await this.$nextTick()

      this.compMap.resize()
    },

    async fetchDirections() {
      try {
        await this.$api.geo(this.fetchDirectionsApi).getDirections({
          startCoordinates: encodeURIComponent(this.address.addressCoordinates.join(',')),
          arriveAt: this.arriveAt,
          paddockId: this.paddock.id
        })

        this.displayDirections(this.fetchDirectionsApi.response.data)
      } catch (error) {
        this.$log.error('Error fetching directions', error)

        this.$notify.error('Error fetching directions, please try again')
      }
    },

    displayDirections(route) {
      if (this.compMap.getSource('route')) {
        this.compMap.removeSource('route')
        this.compMap.removeLayer('route')
      }

      this.compMap.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: route.geometry
        }
      })

      this.compMap.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#00b0ff',
          'line-width': 6,
          'line-opacity': 0.8
        }
      })

      this.setStartMarker(this.address.addressCoordinates)

      this.directions = route.legs[0].steps.map(step => ({
        ...step.maneuver
      }))

      this.routeOverview = {
        distanceMeters: route.distance,
        durationSeconds: route.duration
      }

      if (this.arriveAt) {
        this.routeOverview.leaveAt = this.$date(this.arriveAt).subtract(route.duration, 'seconds').format('HH:mm')
      }

      const bounds = new window.mapboxgl.LngLatBounds()

      route.geometry.coordinates.forEach(lngLat => {
        bounds.extend(lngLat)
      })

      setTimeout(() => {
        try {
          this.compMap.fitBounds(bounds, {
            padding: { top: 50, bottom: 50 }
          })
        } catch (error) {
          this.$log.debug('Error fitting bounds in directions map', error)

          try {
            setTimeout(() => {
              this.compMap.fitBounds(bounds)
            }, 1000)
          } catch (newError) {
            this.$log.debug('Error #2 fitting bounds in directions map', newError)
          }
        }

        this.resize()
      }, 200)
    },

    setStartMarker(coordinates) {
      if (!this.startMarker) {
        const markerElement = document.createElement('div')

        const iconComponent = new IconInstance({
          propsData: {
            icon: faDog,
            wrapperClass: 'text-4xl',
            color: 'blue'
          }
        }).$mount().$el

        markerElement.className = `dog-marker-js`
        markerElement.innerHTML = iconComponent.outerHTML

        this.startMarker = new window.mapboxgl.Marker(markerElement, {
          anchor: 'bottom'
        })

        this.startMarker.setLngLat(coordinates).addTo(this.compMap)
      } else {
        this.startMarker.setLngLat(coordinates)
      }
    },

    setEntranceMarker(coordinates) {
      if (!this.entranceMarker) {
        const markerElement = document.createElement('div')

        const iconComponent = new IconInstance({
          propsData: {
            icon: faCarSolid,
            wrapperClass: 'text-4xl',
            color: 'primary'
          }
        }).$mount().$el

        markerElement.className = `map-marker-js`
        markerElement.innerHTML = iconComponent.outerHTML

        this.entranceMarker = new window.mapboxgl.Marker(markerElement, {
          anchor: 'bottom'
        })

        this.entranceMarker.setLngLat(coordinates).addTo(this.compMap)
      } else {
        this.entranceMarker.setLngLat(coordinates)
      }
    },

    addField(geoJson) {
      if (!geoJson) {
        return false
      }

      this.compMap.addSource('field', {
        type: 'geojson',
        data: geoJson
      })

      this.compMap.addLayer({
        id: 'field',
        type: 'fill',
        source: 'field',
        paint: {
          'fill-color': '#454659',
          'fill-opacity': 0.3
        }
      })

      this.compMap.addLayer({
        id: 'field-outline',
        type: 'line',
        source: 'field',
        paint: {
          'line-color': '#fff',
          'line-width': 2
        }
      })

      // Field labels
      this.compMap.addLayer({
        id: 'field-label',
        type: 'symbol',
        source: 'field',
        layout: {
          'text-field': this.paddock.name,
          'text-size': 15,
          'text-letter-spacing': 0.05
        },
        paint: {
          'text-color': '#fff',
          'text-halo-color': '#000',
          'text-halo-width': 2
        }
      })
    },

    openInMaps() {
      if (this.$app.isIos) {
        this.$modal.open('global/open-maps', {
          destinationCoordinates: this.paddock.entranceCoordinates,
          originCoordinates: this.address.addressCoordinates
        })
      } else {
        openInGoogleMaps(this.address.addressCoordinates, this.paddock.entranceCoordinates, this.$openWindow)
      }
    },

    async fetchCurrentLocation() {
      this.isFetchingLocation = true

      try {
        const position = await getCurrentPosition()

        this.address.addressCoordinates = [position.coords.longitude, position.coords.latitude]

        this.fetchDirections()
      } catch (error) {
        this.$notify.error('Could not fetch your location, do you have it enabled?')

        this.$log.debug('Error fetching position', error?.message)
      }

      this.isFetchingLocation = false
    }
  }
}
